import { useEffect, useState, type ReactNode } from 'react';
import {
  CTA,
  MODAL_BACKDROP,
  MODAL_BODY,
  MODAL_CALLOUT,
  MODAL_CLOSE_X,
  MODAL_FOOT,
  MODAL_HEAD,
  MODAL_ICON_TILE,
  MODAL_KICKER,
  MODAL_PANEL,
  MODAL_TITLE,
} from '@components/ui/designTokens';
import { useSupportConfig } from '../SupportErrorProvider';

/**
 * Wizard de bienvenida que presenta el asistente de soporte con IA. Aparece una
 * sola vez por usuario (se recuerda en localStorage) y solo cuando el asistente
 * está habilitado para el proyecto. Subí SEEN_KEY de versión si querés volver a
 * mostrarlo tras un cambio grande de la funcionalidad.
 *
 * Usa la anatomía de modal del sistema (cabecera con baldosa de icono en
 * degradado + kicker mono, cuerpo, pie gris con las acciones), así se ve igual
 * que cualquier otro modal del portal.
 */
const SEEN_KEY = 'fe-support-welcome-seen-v1';

interface WizardStep {
  icon: ReactNode;
  title: string;
  body: string;
}

const STEPS: WizardStep[] = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a7 7 0 0 0-4 12.75V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.25A7 7 0 0 0 12 2Z" />
        <path d="M9 21h6M10 18v3M14 18v3" />
      </svg>
    ),
    title: 'Conocé tu nuevo asistente de soporte',
    body: 'Sumamos al portal un asistente con inteligencia artificial que te ayuda al instante, sin salir de la pantalla en la que estás. Nada de esperar: preguntale y te responde.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01" />
      </svg>
    ),
    title: 'Preguntá lo que necesites',
    body: 'Tocá el botón flotante de ayuda (abajo a la izquierda), escribí tu duda o describí el problema, y el asistente te responde al instante.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="3 11 22 2 13 21 11 13 3 11" />
      </svg>
    ),
    title: 'Te guío en pantalla',
    body: 'Si hace falta, el asistente te muestra el camino paso a paso: te va señalando dónde tocar dentro del portal hasta resolver lo que buscás.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6 9 17l-5-5" />
      </svg>
    ),
    title: 'Siempre a mano',
    body: 'El botón de ayuda está disponible en todo momento y podés arrastrarlo a donde te quede cómodo. ¿Listo para probarlo?',
  },
];

export function SupportWelcomeWizard() {
  const { assistantEnabled } = useSupportConfig();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  // Mostrar una sola vez, y solo si el asistente está encendido.
  useEffect(() => {
    if (!assistantEnabled) return;
    try {
      if (!localStorage.getItem(SEEN_KEY)) setOpen(true);
    } catch {
      /* localStorage no disponible: no forzamos el modal */
    }
  }, [assistantEnabled]);

  function dismiss() {
    setOpen(false);
    try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* ignore */ }
  }

  // Cerrar con Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') dismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const isLast = step === STEPS.length - 1;
  const current = STEPS[step];

  return (
    <div
      data-support-ui
      className={`${MODAL_BACKDROP} z-[70] flex items-center justify-center p-4`}
      onClick={dismiss}
      role="dialog"
      aria-modal="true"
      aria-labelledby="support-welcome-title"
    >
      <div
        className={`${MODAL_PANEL} flex max-h-[92vh] max-w-md animate-support-in flex-col overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera del sistema: baldosa de icono en degradado, kicker y título. */}
        <div className={MODAL_HEAD}>
          <button onClick={dismiss} aria-label="Cerrar" className={`${MODAL_CLOSE_X} absolute right-6 top-[22px]`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-4 w-4">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
          <div className="flex items-start gap-3.5 pr-12">
            <span className={`${MODAL_ICON_TILE} [&_svg]:h-[18px] [&_svg]:w-[18px]`}>{current.icon}</span>
            <div className="min-w-0">
              <p className={MODAL_KICKER}>NOVEDAD</p>
              <h2 id="support-welcome-title" className={`${MODAL_TITLE} mt-0.5 text-[19px]`}>
                {current.title}
              </h2>
            </div>
          </div>
        </div>

        {/* Cuerpo: el texto del paso, en el bloque destacado del sistema. */}
        <div className={MODAL_BODY}>
          <div className={`${MODAL_CALLOUT} p-4`}>
            <p className="text-[14px] leading-[1.6] text-secondary-600 dark:text-secondary-300">{current.body}</p>
          </div>

          {/* Indicador de pasos (puntos) */}
          <div className="mt-5 flex justify-center gap-2">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                aria-label={`Ir al paso ${i + 1}`}
                className={
                  'h-2 rounded-full transition-all ' +
                  (i === step ? 'w-6 bg-teal-500' : 'w-2 bg-secondary-200 hover:bg-secondary-300 dark:bg-secondary-600')
                }
              />
            ))}
          </div>
        </div>

        {/* Pie del sistema: ayuda a la izquierda, acción primaria a la derecha. */}
        <div className={MODAL_FOOT}>
          {step > 0 ? (
            <button
              onClick={() => setStep((s) => s - 1)}
              className="rounded-[10px] px-3 py-2 text-[13px] font-medium text-secondary-500 transition-colors hover:bg-white hover:text-ink-850 dark:hover:bg-secondary-800 dark:hover:text-white"
            >
              Atrás
            </button>
          ) : (
            <button
              onClick={dismiss}
              className="rounded-[10px] px-3 py-2 text-[13px] font-medium text-secondary-500 transition-colors hover:bg-white hover:text-ink-850 dark:hover:bg-secondary-800 dark:hover:text-white"
            >
              Omitir
            </button>
          )}

          <button onClick={() => (isLast ? dismiss() : setStep((s) => s + 1))} className={CTA}>
            {isLast ? '¡Empezar!' : 'Siguiente'}
          </button>
        </div>
      </div>
    </div>
  );
}
